import { SignageContentType } from '@navio/nsdk';
import { isSignageGrafanaKiosk, type SignageDevicePatchBody, type SignageDeviceRecord } from 'noddi-async';
import { z } from 'zod';

/**
 * One explanation of the config poll field — it is rendered on the pair sheet,
 * the config sheet, the config card, and content assign.
 */
export const SIGNAGE_REFRESH_INTERVAL_TOOLTIP =
  'How often the browser player re-polls its assigned content from the backend (seconds). Updates last seen. Does not control the OS agent heartbeat (~120s).';

/** Config poll interval field, shared by content assign and the device config sheet. */
export const signageRefreshIntervalSecondsSchema = z.preprocess(
  Number,
  z.number().min(10, 'Minimum 10 seconds').max(86_400, 'Maximum 24 hours')
);

/** Shared content-type assign schema (device detail + bulk assign). */
export const signageContentAssignSchema = z
  .object({
    contentType: z.enum(SignageContentType),
    dashboardId: z.number(),
    playlistId: z.number(),
    assignedUrl: z.string(),
    refreshIntervalSeconds: signageRefreshIntervalSecondsSchema
  })
  .superRefine((value, ctx) => {
    if (value.contentType === 'dashboard' && value.dashboardId <= 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['dashboardId'],
        message: 'Select a dashboard'
      });
    }
    if (value.contentType === 'playlist' && value.playlistId <= 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['playlistId'],
        message: 'Select a playlist'
      });
    }
    if (value.contentType === 'url' && !value.assignedUrl.trim()) {
      ctx.addIssue({
        code: 'custom',
        path: ['assignedUrl'],
        message: 'Content URL is required'
      });
    }
  });

export type SignageContentAssignValues = z.input<typeof signageContentAssignSchema>;

type SignageContentAssignOutput = z.output<typeof signageContentAssignSchema>;

export const SIGNAGE_CONTENT_TYPE_OPTIONS = [
  { value: 'none' as const, label: 'No content' },
  { value: 'dashboard' as const, label: 'Dashboard' },
  { value: 'playlist' as const, label: 'Playlist' },
  { value: 'url' as const, label: 'Content URL' }
];

export const emptySignageContentAssignValues = (): SignageContentAssignValues => ({
  contentType: 'none',
  dashboardId: 0,
  playlistId: 0,
  assignedUrl: '',
  refreshIntervalSeconds: 60
});

/** Prefill content-assign form values from a fleet device record. */
export const contentValuesFromDevice = (device: {
  contentType?: SignageContentType | null;
  dashboardId?: number | null;
  playlistId?: number | null;
  assignedUrl?: string | null;
  refreshIntervalSeconds?: number | null;
}): SignageContentAssignValues => ({
  contentType: device.contentType ?? 'none',
  dashboardId: device.dashboardId ?? 0,
  playlistId: device.playlistId ?? 0,
  assignedUrl: device.assignedUrl ?? '',
  refreshIntervalSeconds: device.refreshIntervalSeconds ?? 60
});

/**
 * Content prefill for a pair form. Grafana kiosk devices are pinned on device
 * detail after pairing, so they start from empty content rather than whatever
 * the record still carries.
 */
export const signagePairContentValues = (device: SignageDeviceRecord): SignageContentAssignValues =>
  isSignageGrafanaKiosk(device) ? emptySignageContentAssignValues() : contentValuesFromDevice(device);

/**
 * Content half of a pair body. Grafana dashboards are pinned on device detail
 * after pair — never via the pair body.
 */
export const signagePairContentBody = (
  values: SignageContentAssignOutput,
  isGrafanaKiosk: boolean
): SignageDevicePatchBody =>
  signageContentAssignToBody(
    isGrafanaKiosk ? signageContentAssignSchema.parse(emptySignageContentAssignValues()) : values
  );

/**
 * The building binding (posted as garageId) and content bindings only exist
 * within a service department, so every form that changes organization /
 * department has to reset them together.
 */
export const clearSignageDepartmentScopedFields = (form: {
  setFieldValue: (name: 'garageId' | 'dashboardId' | 'playlistId' | 'contentType', value: number | 'none') => void;
}) => {
  form.setFieldValue('garageId', 0);
  form.setFieldValue('dashboardId', 0);
  form.setFieldValue('playlistId', 0);
  form.setFieldValue('contentType', 'none');
};

const clearedContentBindings = {
  playlistId: null,
  dashboardId: null,
  contentPresetId: null,
  // Backend URLField rejects "" — use null to clear (pair / patch / bulk-assign).
  assignedUrl: null
} as const;

/**
 * Complete content payload: always clears competing bindings so playlist /
 * dashboard / URL / none transitions do not rely on backend side-effects.
 * Safe for bulk assign and single-device PATCH.
 */
export const signageContentAssignToBody = (value: SignageContentAssignOutput): SignageDevicePatchBody => {
  const binding =
    value.contentType === 'playlist'
      ? { playlistId: value.playlistId }
      : value.contentType === 'dashboard'
        ? { dashboardId: value.dashboardId }
        : value.contentType === 'url'
          ? { assignedUrl: value.assignedUrl.trim() }
          : {};
  return {
    ...clearedContentBindings,
    refreshIntervalSeconds: value.refreshIntervalSeconds,
    ...binding
  };
};
